import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowRight, Loader2 } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEASERS = [
  "Do you have a marketing problem — or a leadership problem?",
  "Do you know your CAC-to-LTV ratio?",
  "Are you creating experiences, not just impressions?",
  "What would a CMO change in your next 90 days?",
];

const Playbook = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/playbook`, { email: email.trim() });
      setDownloadUrl(res.data.download_url);
      toast.success("Your playbook is ready — it's also on its way to your inbox.");
    } catch {
      toast.error("Something went wrong — please try again.");
    }
    setLoading(false);
  };

  return (
    <section id="playbook" data-testid="playbook-section" className="px-6 md:px-12 py-24 md:py-36 border-t border-line">
      <div className="mx-auto max-w-[100rem] grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <SectionHead
            index="08"
            label="Free Download"
            title={<>The Fractional CMO <em className="text-taupe">Playbook.</em></>}
            className="mb-10 md:mb-12"
          />
          <Reveal delay={0.15}>
            <p className="text-base md:text-lg font-light text-ash leading-relaxed max-w-md">
              Ten questions to ask before you hire marketing leadership — the same
              lens Aspen uses in the first 30 days with a new client. A preview of
              what's inside:
            </p>
          </Reveal>
          <div className="mt-10">
            {TEASERS.map((t, i) => (
              <Reveal key={t} delay={0.2 + i * 0.05}>
                <p className="flex gap-4 border-t border-line py-4 font-serif italic text-lg md:text-xl font-light text-ink/80">
                  <span className="text-taupe not-italic font-sans text-xs tracking-[0.2em] pt-1.5 shrink-0">
                    Q{i + 1 < 10 ? `0${i + 1}` : i + 1}
                  </span>
                  {t}
                </p>
              </Reveal>
            ))}
            <div className="border-t border-line" />
          </div>
        </div>

        <Reveal delay={0.2}>
          <div className="lg:sticky lg:top-28 border border-line p-8 md:p-12 bg-white/40">
            <p className="overline-label">Get the playbook</p>
            <h3 className="mt-4 font-serif text-3xl md:text-4xl font-light tracking-tight leading-tight">
              Where should we send it?
            </h3>
            {downloadUrl ? (
              <div data-testid="playbook-success" className="mt-8">
                <p className="text-sm font-light text-ash leading-relaxed">
                  Your copy is ready — and a download link is on its way to <em className="font-serif text-ink">{email}</em>.
                </p>
                <a
                  data-testid="playbook-download-link"
                  href={downloadUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-8 inline-flex w-full items-center justify-center gap-3 bg-ink text-paper text-xs uppercase tracking-[0.25em] px-8 py-5 hover:bg-taupe transition-colors duration-300"
                >
                  Download the Playbook
                  <ArrowDownToLine size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                </a>
              </div>
            ) : (
              <form data-testid="playbook-form" onSubmit={onSubmit} className="mt-8">
                <label htmlFor="pb-email" className="text-[10px] uppercase tracking-[0.25em] text-ash">
                  Email address *
                </label>
                <input
                  id="pb-email"
                  data-testid="playbook-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-3 w-full bg-transparent border-b border-ink/20 focus:border-ink outline-none py-3 text-base font-light placeholder:text-ash/50 transition-colors duration-300"
                />
                <button
                  data-testid="playbook-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="group mt-8 inline-flex w-full items-center justify-center gap-3 bg-ink text-paper text-xs uppercase tracking-[0.25em] px-8 py-5 hover:bg-taupe disabled:opacity-50 transition-colors duration-300"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : (
                    <>Send Me the Playbook <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" /></>
                  )}
                </button>
                <p className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-ash">
                  Free · No newsletter, no spam
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Playbook;
